/**
 * =============================================================================
 * Componente: HeroBalanceCard
 * Versão: 2.0.0 (Clareza financeira — Em processamento + Total ganho)
 *
 * Mudanças v2.0.0:
 *   - Removido o chip duplicado "R$ X no mês" (desktop e mobile). A
 *     informação já existe no card "Progresso do mês" logo abaixo, e
 *     gerava confusão visual.
 *   - Adicionado bloco "Em processamento" no rodapé, ao lado de
 *     "Total ganho", refletindo o `in_transit` do ledger (payouts em
 *     `pending`/`processing`). Resolve a dor: quando o afiliado solicita
 *     saque, o saldo zera e ele perdia a noção de para onde foi.
 *   - Tooltips explicativos em cada métrica para evitar dúvidas.
 *   - "Em processamento" só renderiza quando > 0 (evita ruído visual).
 *
 * Descrição:
 *   Hero do dashboard do afiliado. Gradiente premium, número monetário em
 *   display size, três blocos resumo (Disponível em destaque, Em
 *   processamento e Total ganho) e botão "Sacar agora".
 * =============================================================================
 */
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { KpiInfoTooltip } from '@/components/affiliate/shared/KpiInfoTooltip';
import { cn } from '@/lib/utils';

interface HeroBalanceCardProps {
  isLoading?: boolean;
  /** Saldo disponível para saque (comissões aprovadas sem payout). */
  availableBalance: number;
  /** Valor já solicitado e aguardando processamento financeiro. */
  inTransit: number;
  /** Total acumulado ganho ao longo da vida do afiliado. */
  totalEarned: number;
  greeting: string;
  firstName: string;
}

/** Formata número para BRL com 2 casas. */
function fmtBRL(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  });
}

/**
 * Pequeno bloco de métrica secundária no rodapé do hero, com tooltip.
 * Mantém visual coeso com o gradiente: texto branco + opacidade.
 * Usa KpiInfoTooltip (componente compartilhado) para padronizar tipografia
 * e comportamento de colisão dos tooltips.
 */
function FooterStat({
  label,
  value,
  hint,
  align = 'left',
}: {
  label: string;
  value: string;
  hint: string;
  align?: 'left' | 'right';
}) {
  return (
    <div className={cn('min-w-0', align === 'right' && 'text-right')}>
      <div
        className={cn(
          'flex items-center gap-1',
          align === 'right' && 'justify-end',
        )}
      >
        <p className="text-[10.5px] font-medium uppercase tracking-wider opacity-75">
          {label}
        </p>
        {/* side="top": estes stats estão no rodapé do hero — abrir para
            baixo colidiria com o card seguinte. Acima há espaço dentro do
            próprio hero. */}
        <KpiInfoTooltip label={label} hint={hint} side="top" />
      </div>
      <p className="text-[15px] font-semibold tabular-nums">{value}</p>
    </div>
  );
}

export function HeroBalanceCard({
  isLoading,
  availableBalance,
  inTransit,
  totalEarned,
  greeting,
  firstName,
}: HeroBalanceCardProps) {
  // Renderiza "Em processamento" apenas quando há valor — evita ocupar
  // espaço com bloco zerado e mantém o card mais limpo no caso comum.
  const showInTransit = inTransit > 0;

  return (
    <section
      aria-label="Saldo disponível"
      className={cn(
        'aff-hero-surface aff-animate-in',
        'rounded-[var(--aff-radius-lg)] px-5 pt-5 pb-6 md:px-7 md:pt-6 md:pb-7',
      )}
    >
      {/* Saudação no topo (apenas desktop — no mobile o header global cuida disso) */}
      <div className="hidden md:block mb-3">
        <p className="text-[13px] font-medium opacity-90">
          {greeting}, {firstName} 👋
        </p>
      </div>

      {/* Linha do saldo: valor à esquerda + CTA "Sacar agora" à direita.
          No mobile o CTA fica ancorado no topo da linha (alinhado com o
          valor) para liberar a faixa inferior por completo às métricas
          secundárias, evitando sobreposição. */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="text-[11.5px] font-semibold uppercase tracking-[0.18em] opacity-80">
              Saldo disponível
            </p>
            {/* Tooltip explicativo padronizado via KpiInfoTooltip */}
            <KpiInfoTooltip
              label="Saldo disponível"
              hint="Total de comissões aprovadas e ainda não solicitadas para saque. É o valor que você pode sacar agora, respeitando o mínimo de saque do programa."
            />
          </div>
            {isLoading ? (
              <Skeleton className="mt-2 h-10 md:h-12 w-40 md:w-48 bg-white/20" />
            ) : (
              // Tamanhos reduzidos (de 36/52 → 28/40) para acomodar
              // valores grandes (ex.: R$ 1.234.567,89) sem quebrar
              // o layout do hero.
              <p className="mt-1.5 text-[28px] md:text-[40px] font-bold leading-none tabular-nums tracking-tight break-all">
                {fmtBRL(availableBalance)}
              </p>
            )}
          </div>

          <Button
            asChild
            size="sm"
            className={cn(
              'rounded-full font-semibold gap-1.5 shrink-0 self-start mt-1',
              // Mobile compacto (ícone + texto curto), desktop confortável
              'h-9 px-3 text-[12.5px] md:h-10 md:px-4 md:text-sm',
              'bg-white text-[hsl(var(--aff-primary))] hover:bg-white/90',
              'shadow-lg shadow-black/10',
            )}
          >
            <Link to="/afiliado/saques">
              <span className="hidden sm:inline">Sacar agora</span>
              <span className="sm:hidden">Sacar</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>

        {/* Rodapé: métricas secundárias em largura total — sempre abaixo do
            saldo, sem disputar espaço com o CTA. No mobile usamos grid de
            2 colunas para garantir que "Em processamento" e "Total ganho"
            não se sobreponham nem sejam truncados. */}
        <div
          className={cn(
            'mt-5 grid gap-4',
            showInTransit ? 'grid-cols-2' : 'grid-cols-1',
          )}
        >
          {showInTransit && (
            <FooterStat
              label="Em processamento"
              value={fmtBRL(inTransit)}
              hint="Saques que você já solicitou e estão sendo processados pelo financeiro. Caso o saque seja cancelado, o valor volta automaticamente para o Saldo disponível."
            />
          )}
          <FooterStat
            label="Total ganho"
            value={fmtBRL(totalEarned)}
            hint="Tudo que você já acumulou em comissões aprovadas ao longo da vida da sua conta — soma de pago, disponível e em processamento."
          />
        </div>
      </section>
  );
}
